/**
 * 「提出して採点」の結果。エディターの下、提出の釦の上に出す。
 *
 * 帯 (11 / 11 通過) と、テストごとの札と、選んだテストの 1 行でできている。テストが増えても高さは増えず、札は横に並んで、足りなければ横に送る。
 * 作りは PR #3583 の CodingCaseResult (例ごとの札) に合わせてある。向こうの部品が main に入ったら、
 * views (StdioCaseView は向こうの CaseView と同じ形) をそのまま渡して置き換えられる。それまでは、札の作りだけここに持つ。
 *
 * 隠れたテストの中身は出さない。出すのは、何番目か、どの群か、どの種類で落ちたか (lib/coding-test-result.ts)。
 * 字はスマホで 17px 以上。読み上げ用の字 (sr-only) は絶対配置なので、釦を relative にして、横に送る枠の外へ漏らさない (漏れると頁の横幅が広がる)
 */
import { type KeyboardEvent, useEffect, useId, useState } from "react";
import { Check, LoaderCircle, X } from "lucide-react";
import { phraseText } from "@/lib/coding-interview";
import type { StdioCaseView } from "@/lib/coding-test-result";

const WRAP = "[word-break:keep-all] [overflow-wrap:anywhere]";

export type StdioSubmitResultProps = {
  /** 提出の前 (idle)、採点の最中 (checking)、結果が出た (judged) */
  state: "idle" | "checking" | "judged";
  /** 採点の最中に出す段階の文 */
  progress: string | null;
  views: readonly StdioCaseView[] | null;
  /** 帯の右の 1 文 (通ったときと、直すときで替える) */
  passLine: string;
  fixLine: string;
  idleHint: string;
  /** 最も遅いテストの時間と、時間切れのときの手がかり */
  note: string | null;
  /** 新しく採点するたびに変える。選んでいる札を、落ちたテストの先頭へ戻す */
  resetKey: unknown;
};

export function StdioSubmitResult({ state, progress, views, passLine, fixLine, idleHint, note, resetKey }: StdioSubmitResultProps) {
  const base = useId();
  const list = views ?? [];
  const passedCount = list.filter((v) => v.passed).length;
  const allPassed = list.length > 0 && passedCount === list.length;
  const firstFailed = list.findIndex((v) => !v.passed);
  const [picked, setPicked] = useState<number | null>(null);
  useEffect(() => setPicked(null), [resetKey]);
  const active = picked ?? (firstFailed >= 0 ? firstFailed : 0);

  if (state === "idle") {
    return (
      <p data-stdio-result="idle" className={`m-0 rounded-xl border border-main bg-surface px-4 py-3 text-[17px] leading-[1.6] text-sub ${WRAP}`}>
        <span className="font-bold text-main">できているか</span>
        <span className="ml-2">{phraseText(idleHint)}</span>
      </p>
    );
  }
  if (state === "checking") {
    return (
      <p
        data-stdio-result="checking"
        role="status"
        className={`m-0 flex items-center gap-2 rounded-xl border border-main bg-surface px-4 py-3 text-[17px] leading-[1.6] text-main ${WRAP}`}
      >
        <LoaderCircle className="h-5 w-5 shrink-0 animate-spin text-sub" aria-hidden="true" />
        {phraseText(progress ?? "採点しています")}
      </p>
    );
  }

  const move = (event: KeyboardEvent<HTMLDivElement>) => {
    const step = event.key === "ArrowRight" ? 1 : event.key === "ArrowLeft" ? -1 : 0;
    const home = event.key === "Home";
    const end = event.key === "End";
    if (!step && !home && !end) return;
    event.preventDefault();
    const next = home ? 0 : end ? list.length - 1 : (active + step + list.length) % list.length;
    setPicked(next);
    event.currentTarget.querySelectorAll<HTMLElement>('[role="tab"]')[next]?.focus();
  };

  const view = list[active];
  return (
    <div
      data-stdio-result={allPassed ? "pass" : "wrong"}
      aria-live="polite"
      className={`overflow-hidden rounded-xl border bg-surface [word-break:auto-phrase] ${allPassed ? "border-primary" : "border-error"}`}
    >
      <p className={`m-0 flex flex-wrap items-center gap-x-3 gap-y-1 px-4 py-3 text-[17px] font-bold leading-snug ${allPassed ? "bg-second" : "bg-error-light"}`}>
        <span className="flex items-center gap-1.5 tabular-nums text-main">
          {allPassed ? <Check className="h-6 w-6 shrink-0 text-primary" aria-hidden="true" /> : <X className="h-6 w-6 shrink-0 text-error" aria-hidden="true" />}
          {passedCount} / {list.length} 通過
        </span>
        <span className="font-normal text-main">{allPassed ? passLine : fixLine}</span>
      </p>
      <div role="tablist" aria-label="テストごとの結果" onKeyDown={move} className="flex overflow-x-auto border-b border-main px-2">
        {list.map((item, index) => {
          const selected = index === active;
          return (
            <button
              key={item.label}
              type="button"
              role="tab"
              id={`${base}-tab-${index}`}
              aria-selected={selected}
              aria-controls={`${base}-panel`}
              tabIndex={selected ? 0 : -1}
              onClick={() => setPicked(index)}
              data-stdio-tab={index}
              data-passed={item.passed}
              className={`relative flex min-h-11 shrink-0 items-center gap-1.5 whitespace-nowrap border-b-2 px-3 text-[17px] ${
                selected ? "border-primary font-bold text-main" : "border-transparent text-sub hover:text-main"
              }`}
            >
              {item.passed ? <Check className="h-5 w-5 shrink-0 text-primary" aria-hidden="true" /> : <X className="h-5 w-5 shrink-0 text-error" aria-hidden="true" />}
              <span className="sr-only">{item.passed ? "通過" : "不通過"}</span>
              {item.label.replace(/^テスト /, "").replace(/（.*）$/, "")}
            </button>
          );
        })}
      </div>
      <div role="tabpanel" id={`${base}-panel`} aria-labelledby={`${base}-tab-${active}`} className="px-4 pb-4 pt-3">
        {view ? (
          <p className={`m-0 text-[17px] leading-[1.7] text-main ${WRAP}`} data-stdio-detail>
            <span className="font-bold">{view.label}</span>
            <span className="ml-2">{view.passed ? "通りました" : phraseText(view.error ?? "通りませんでした")}</span>
          </p>
        ) : null}
        {note ? <p className={`m-0 mt-2 text-[17px] leading-[1.7] text-sub ${WRAP}`}>{phraseText(note)}</p> : null}
      </div>
    </div>
  );
}
